/**
 * CMG Collection Filter AJAX
 * --------------------------
 * <cmg-collection-view> wraps sections/main-collection-product-grid.liquid
 * and upgrades its native GET filter form to seamless updates, mirroring the
 * architecture of Dawn's <facet-filters-form>:
 *
 *  - Any change to the facet form (or the toolbar sort select, associated
 *    via the HTML form="" attribute) serialises the form and fetches the
 *    same collection URL through the Section Rendering API
 *    (?section_id=...), then swaps the grid, count and facet sidebar from
 *    the response.
 *  - Clicks on active-filter pills, "clear all" and pagination links are
 *    intercepted and routed through the same renderer, so every control
 *    still works as a plain link without JavaScript.
 *  - URL state is kept in sync via history.pushState / popstate.
 *  - Failures show a user-visible notification with a full-page-reload
 *    fallback link; the previous grid state is left untouched.
 *
 * Global namespace: window.CMGTheme
 * Events (dispatched on document):
 *  - 'cmg:collection:updated' { sectionId, searchParams, productCountText }
 *  - 'cmg:collection:error'   { sectionId, error }
 */

window.CMGTheme = window.CMGTheme || {};
window.CMGTheme.events = Object.assign({}, window.CMGTheme.events, {
  COLLECTION_UPDATED: 'cmg:collection:updated',
  COLLECTION_ERROR: 'cmg:collection:error',
});

const SELECTORS = {
  form: 'form.cmg-facets',
  facetLinks: '[data-cmg-facet-link]',
  paginationLinks: 'a.pagination__item',
  facetGroups: 'details.cmg-facets__group',
  errorRegion: '.cmg-shop__error',
};

const DEBOUNCE_MS = { checkbox: 150, text: 500 };

class CmgCollectionView extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.collectionUrl = this.dataset.collectionUrl || window.location.pathname;
    this.debounceTimer = null;
    this.abortController = null;

    this.onChangeBound = this.onFormChange.bind(this);
    this.onClickBound = this.onLinkClick.bind(this);
    this.onSubmitBound = this.onFormSubmit.bind(this);
    this.onPopStateBound = this.onPopState.bind(this);

    this.addEventListener('change', this.onChangeBound);
    this.addEventListener('click', this.onClickBound);
    this.addEventListener('submit', this.onSubmitBound);
    window.addEventListener('popstate', this.onPopStateBound);

    // Sort select lives outside this element in DOM terms only when themes
    // move the toolbar; it is form-associated, so listen on it directly too.
    const sortSelect = document.getElementById(`CmgSortBy-${this.sectionId}`);
    if (sortSelect && !this.contains(sortSelect)) {
      sortSelect.addEventListener('change', this.onChangeBound);
    }
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.onPopStateBound);
    if (this.abortController) this.abortController.abort();
  }

  get form() {
    return this.querySelector(SELECTORS.form);
  }

  onFormSubmit(event) {
    if (event.target !== this.form) return;
    event.preventDefault();
    this.applyFilters();
  }

  onFormChange(event) {
    const input = event.target;
    const belongsToForm =
      this.form && (this.form.contains(input) || input.form === this.form);
    if (!belongsToForm) return;

    const delay =
      input.type === 'number' || input.type === 'text'
        ? DEBOUNCE_MS.text
        : DEBOUNCE_MS.checkbox;

    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => this.applyFilters(), delay);
  }

  onLinkClick(event) {
    const link = event.target.closest(
      `${SELECTORS.facetLinks}, ${SELECTORS.paginationLinks}`
    );
    if (!link || !link.href || !this.contains(link)) return;

    event.preventDefault();
    const url = new URL(link.href, window.location.origin);
    const isPagination = link.matches(SELECTORS.paginationLinks);
    this.renderPage(url.searchParams.toString(), {
      updateHistory: true,
      scrollToTop: isPagination,
    });
  }

  onPopState(event) {
    const searchParams =
      event.state && typeof event.state.cmgSearchParams === 'string'
        ? event.state.cmgSearchParams
        : window.location.search.replace(/^\?/, '');
    this.renderPage(searchParams, { updateHistory: false });
  }

  applyFilters() {
    if (!this.form) return;
    const params = new URLSearchParams(new FormData(this.form));
    this.renderPage(params.toString(), { updateHistory: true });
  }

  async renderPage(searchParams, { updateHistory = true, scrollToTop = false } = {}) {
    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();

    this.setAttribute('aria-busy', 'true');
    this.clearError();

    const requestUrl = `${this.collectionUrl}?${searchParams}${
      searchParams ? '&' : ''
    }section_id=${encodeURIComponent(this.sectionId)}`;
    const documentUrl = `${this.collectionUrl}${searchParams ? `?${searchParams}` : ''}`;

    try {
      const response = await fetch(requestUrl, {
        signal: this.abortController.signal,
      });
      if (!response.ok) {
        throw new Error(`Section render failed with status ${response.status}`);
      }

      const html = await response.text();
      this.renderSectionFromResponse(html);

      if (updateHistory) {
        history.pushState({ cmgSearchParams: searchParams }, '', documentUrl);
      }
      if (scrollToTop) {
        this.scrollIntoView({ behavior: 'auto', block: 'start' });
      }

      const countEl = document.getElementById(`CmgProductCount-${this.sectionId}`);
      document.dispatchEvent(
        new CustomEvent(window.CMGTheme.events.COLLECTION_UPDATED, {
          detail: {
            sectionId: this.sectionId,
            searchParams,
            productCountText: countEl ? countEl.textContent.trim() : null,
          },
        })
      );
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.showError(documentUrl);
      document.dispatchEvent(
        new CustomEvent(window.CMGTheme.events.COLLECTION_ERROR, {
          detail: { sectionId: this.sectionId, error },
        })
      );
    } finally {
      this.removeAttribute('aria-busy');
    }
  }

  /**
   * Swaps grid, count and facet sidebar from a Section Rendering API
   * response. Accordion open/closed state is restored by index (the filter
   * order is stable across renders) and focus is restored by element id so
   * keyboard users are not dropped back to <body> after each change.
   */
  renderSectionFromResponse(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const activeElementId = document.activeElement ? document.activeElement.id : null;
    const openStates = Array.from(this.querySelectorAll(SELECTORS.facetGroups)).map(
      (group) => group.open
    );

    [
      `CmgProductGridContainer-${this.sectionId}`,
      `CmgProductCount-${this.sectionId}`,
      `CmgFacetsContainer-${this.sectionId}`,
    ].forEach((id) => {
      const current = document.getElementById(id);
      const next = parsed.getElementById(id);
      if (current && next) current.innerHTML = next.innerHTML;
    });

    this.querySelectorAll(SELECTORS.facetGroups).forEach((group, index) => {
      if (typeof openStates[index] === 'boolean') group.open = openStates[index];
    });

    if (activeElementId) {
      const target = document.getElementById(activeElementId);
      if (target) target.focus({ preventScroll: true });
    }
  }

  showError(fallbackUrl) {
    this.clearError();
    const region = document.createElement('div');
    region.className = 'cmg-shop__error';
    region.setAttribute('role', 'alert');

    const message = document.createElement('span');
    message.textContent = this.dataset.errorMessage || 'Something went wrong.';

    // Full-page navigation fallback: a plain GET of the same filtered URL,
    // bypassing the Section Rendering API entirely.
    const retry = document.createElement('a');
    retry.className = 'cmg-shop__error-retry';
    retry.href = fallbackUrl;
    retry.textContent = this.dataset.errorRetry || 'Reload';

    region.append(message, retry);
    const layout = this.querySelector('.cmg-shop__layout');
    if (layout) {
      layout.before(region);
    } else {
      this.prepend(region);
    }
  }

  clearError() {
    const existing = this.querySelector(SELECTORS.errorRegion);
    if (existing) existing.remove();
  }
}

if (!customElements.get('cmg-collection-view')) {
  customElements.define('cmg-collection-view', CmgCollectionView);
}
